import { useState, type ReactNode } from 'react';
import { Activity, ChevronRight, CircleDollarSign, FileWarning, Hourglass, Layers, ShieldCheck, X, Zap } from 'lucide-react';
import { Card } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { useAutoaprendizado } from '../autoaprendizado-api';
import type { Autoaprendizado, Regra } from '../autoaprendizado-schemas';
import { AgentesModuleTabs } from './AgentesModuleTabs';

const TABS = ['Dashboard', 'Obediência', 'Quarentena', 'Erros & quebras', 'Melhorias'] as const;
type Tab = (typeof TABS)[number];

const CARD = 'rounded-[14px] border border-border bg-elevated dark:border-transparent';

function fmt(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString('pt-BR');
}
function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-muted">{children}</p>;
}
type Tone = 'ok' | 'warning' | 'danger' | 'info' | 'accent' | 'muted';
const TONE: Record<Tone, string> = {
  ok: 'var(--ok)', warning: 'var(--warning)', danger: 'var(--danger)', info: 'var(--info)', accent: 'var(--accent)', muted: 'var(--text-muted)',
};
function Badge({ children, tone = 'muted' }: { children: ReactNode; tone?: Tone }) {
  return (
    <span
      className="inline-flex w-fit items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
      style={{ color: TONE[tone], background: `color-mix(in srgb, ${TONE[tone]} 14%, transparent)` }}
    >
      {children}
    </span>
  );
}
function Dot({ tone }: { tone: Tone }) {
  return <span className="inline-block size-[9px] shrink-0 rounded-full" style={{ background: TONE[tone] }} />;
}

function impactoTone(priority: string | null): Tone {
  return priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'muted';
}
function impactoLabel(priority: string | null): string {
  return priority === 'high' ? 'Critical' : priority === 'medium' ? 'Medium' : 'Low';
}
// status derivado p/ a tabela de obediência (dado real: promoted + evidência)
function statusView(r: Regra): { label: string; tone: Tone } {
  if (r.status !== 'promoted') {
    if (r.status === 'wont_fix') return { label: 'Descartada', tone: 'muted' };
    if (r.status === 'keep') return { label: 'Mantida', tone: 'info' };
    return { label: r.status ?? '—', tone: 'muted' };
  }
  if ((r.application_count ?? 0) > 0) return { label: 'Saudável', tone: 'ok' };
  return { label: 'Sem evidência', tone: 'warning' };
}

// ─── KPI ───
function Kpi({ icon: Icon, label, value, unit, foot, footTone }: {
  icon: typeof Activity; label: string; value: string; unit?: string; foot?: string; footTone?: Tone;
}) {
  return (
    <div className={cn('flex flex-1 basis-[160px] flex-col gap-2.5 p-4', CARD)}>
      <span className="flex size-8 items-center justify-center rounded-[8px] bg-[var(--accent)]">
        <Icon size={16} strokeWidth={1.9} className="text-[var(--on-solid)]" />
      </span>
      <span className="text-[10.5px] font-medium uppercase tracking-[0.04em] text-text-muted">{label}</span>
      <div className="flex items-baseline gap-1">
        <span className="text-[22px] font-bold tracking-tight text-text">{value}</span>
        {unit && <span className="text-xs font-medium text-text-muted">{unit}</span>}
      </div>
      {foot && (
        <span className="flex items-center gap-1.5 text-[11px] text-text-muted">
          {footTone && <Dot tone={footTone} />}
          {foot}
        </span>
      )}
    </div>
  );
}

// ─── Dashboard ───
function DashboardTab({ d }: { d: Autoaprendizado }) {
  const r = d.resumo;
  const imp = d.impacto;
  const impTotal = imp.critical + imp.medium + imp.low || 1;
  const segs: Array<{ label: string; v: number; tone: Tone }> = [
    { label: 'Critical', v: imp.critical, tone: 'danger' },
    { label: 'Medium', v: imp.medium, tone: 'warning' },
    { label: 'Low', v: imp.low, tone: 'muted' },
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-3.5">
        <Kpi icon={Layers} label="Regras promovidas" value={fmt(r.regras_promovidas)} foot="auditadas" footTone="accent" />
        <Kpi icon={Hourglass} label="Em quarentena" value={fmt(r.em_quarentena)} foot="7 dias observando" footTone="warning" />
        <Kpi icon={ShieldCheck} label="Quebras ativas" value={fmt(r.quebras_ativas)} foot={r.quebras_ativas ? 'atenção' : 'nenhuma'} footTone={r.quebras_ativas ? 'danger' : 'ok'} />
        <Kpi icon={FileWarning} label="Erros ativos" value={fmt(r.erros_ativos)} foot={r.erros_ativos ? 'atenção' : 'nenhum'} footTone={r.erros_ativos ? 'danger' : 'ok'} />
        <Kpi icon={Zap} label="Melhorias no backlog" value={fmt(r.feature_backlog)} foot="triadas" footTone="info" />
        <Kpi icon={CircleDollarSign} label="Pattern-Key duplicados" value={fmt(r.pattern_key_duplicados)} foot={r.pattern_key_duplicados ? 'conflito' : 'sem conflito'} footTone={r.pattern_key_duplicados ? 'danger' : 'ok'} />
      </div>

      <div className="flex flex-wrap items-stretch gap-4">
        {/* Obediência */}
        <div className={cn('flex flex-1 basis-[320px] flex-col gap-4 p-[22px]', CARD)}>
          <SectionLabel>Obediência das regras</SectionLabel>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {[
              ['Regras auditadas', d.obediencia.regras_auditadas, 'muted'],
              ['Com sinal em memória', d.obediencia.com_sinal_memoria, 'ok'],
              ['Sem evidência suficiente', d.obediencia.sem_evidencia_suficiente, 'warning'],
              ['Quebras potenciais', d.obediencia.quebras_potenciais, 'muted'],
            ].map(([lb, v, t]) => (
              <div key={lb as string} className="flex flex-col gap-1">
                <span className="text-[19px] font-bold text-text" style={{ color: (v as number) ? TONE[t as Tone] : undefined }}>{fmt(v as number)}</span>
                <span className="text-[11.5px] text-text-muted">{lb as string}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Impacto */}
        <div className={cn('flex flex-1 basis-[320px] flex-col gap-4 p-[22px]', CARD)}>
          <SectionLabel>Impacto das regras</SectionLabel>
          <div className="flex h-4 w-full gap-[3px] overflow-hidden rounded-full">
            {segs.filter((x) => x.v > 0).map((x) => (
              <div key={x.label} className="h-full" style={{ flexGrow: x.v, background: TONE[x.tone] }} />
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {segs.map((x) => (
              <div key={x.label} className="flex items-center gap-2.5">
                <Dot tone={x.tone} />
                <span className="flex-1 text-[12.5px] font-medium text-text">{x.label}</span>
                <span className="text-[12.5px] font-semibold text-text">{x.v}</span>
                <span className="text-[11.5px] text-text-muted">{x.v ? Math.round((x.v / impTotal) * 100) + '%' : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Atenção + próxima ação */}
      <div className="flex flex-wrap items-stretch gap-4">
        <div className={cn('flex basis-[460px] grow-0 flex-col px-[22px] pb-3.5 pt-5 max-md:basis-full', CARD)}>
          <SectionLabel>Requer atenção</SectionLabel>
          <div className="mt-2 flex flex-col">
            {d.alertas.length === 0 && <p className="py-4 text-sm text-text-muted">Nenhum alerta estrutural.</p>}
            {d.alertas.map((a, i) => (
              <div key={i} className={cn('flex items-center gap-2.5 py-2.5', i > 0 && 'border-t border-border/70')}>
                <Dot tone="warning" />
                <span className="text-[12.5px] font-medium text-text first-letter:uppercase">{a}</span>
              </div>
            ))}
          </div>
        </div>
        <div
          className="flex flex-1 basis-[320px] flex-col gap-3 rounded-[14px] p-[22px]"
          style={{ background: 'color-mix(in srgb, var(--accent) 8%, var(--elevated))', border: '1.2px solid color-mix(in srgb, var(--accent) 30%, transparent)' }}
        >
          <SectionLabel>Próxima ação recomendada</SectionLabel>
          <p className="text-[17px] font-semibold text-text">Registre Application-Count e Last-Applied quando uma regra for obedecida.</p>
          <p className="text-[12.5px] text-text-2">
            Sobe a cobertura de evidência dos atuais {fmt(r.cobertura_evidencia_pct)}% e libera as {fmt(r.em_quarentena)} regras em quarentena aguardando sinal real de aplicação.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Obediência (tabela) ───
function ObedienciaTab({ d, onSel }: { d: Autoaprendizado; onSel: (r: Regra) => void }) {
  const regras = d.regras.filter((r) => r.status === 'promoted');
  return (
    <div className={cn('flex flex-col px-[22px]', CARD)}>
      <div className="grid grid-cols-[minmax(160px,1.4fr)_2.6fr_92px_120px_120px_100px] items-center gap-3 border-b border-border py-3 text-[10.5px] font-semibold uppercase tracking-wide text-text-muted max-lg:hidden">
        <span>Pattern-Key</span><span>Resumo</span><span>Impacto</span><span>Status</span><span>Evidência</span><span className="text-right">Últ. aplic.</span>
      </div>
      {regras.map((r, i) => {
        const st = statusView(r);
        return (
          <button
            key={r.id ?? i}
            type="button"
            onClick={() => onSel(r)}
            className={cn('grid grid-cols-[minmax(160px,1.4fr)_2.6fr_92px_120px_120px_100px] items-center gap-3 py-3 text-left transition-colors hover:bg-surface2 max-lg:grid-cols-1 max-lg:gap-1', i > 0 && 'border-t border-border/70')}
          >
            <span className="truncate text-[11.5px] font-medium text-[var(--accent)]">{r.pattern_key}</span>
            <span className="truncate text-[12.5px] text-text">{r.summary}</span>
            <span><Badge tone={impactoTone(r.priority)}>{impactoLabel(r.priority)}</Badge></span>
            <span><Badge tone={st.tone}>{st.label}</Badge></span>
            <span className="text-[12px] text-text-2">{(r.application_count ?? 0) > 0 ? `${r.application_count} aplic.` : 'sem evidência'}</span>
            <span className="text-right text-[12px] text-text-muted">{r.last_applied ?? '—'}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Quarentena ───
function QuarentenaTab({ d, onSel }: { d: Autoaprendizado; onSel: (r: Regra) => void }) {
  const regras = d.regras.filter((r) => r.status === 'promoted');
  const o = d.obediencia;
  const states: Array<[string, number | null, Tone]> = [
    ['Com sinal em memória', o.com_sinal_memoria, 'info'],
    ['Sem evidência suficiente', o.sem_evidencia_suficiente, 'warning'],
    ['Quebra detectada', o.quebras_potenciais, 'danger'],
    ['Sem evidência pós-quarentena', o.sem_evidencia_apos_quarentena, 'muted'],
  ];
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-3.5">
        {states.map(([lb, v, t]) => (
          <div key={lb} className={cn('flex flex-1 basis-[200px] items-center gap-3 p-4', CARD)}>
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: TONE[t] }} />
            <div className="flex flex-col">
              <span className="text-[20px] font-bold text-text">{fmt(v)}</span>
              <span className="text-[11px] text-text-muted">{lb}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {regras.map((r, i) => (
          <button key={r.id ?? i} type="button" onClick={() => onSel(r)} className={cn('flex items-center gap-4 p-[18px] text-left transition-colors hover:bg-surface2', CARD)}>
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="flex items-center gap-2.5">
                <span className="text-[12px] font-semibold text-[var(--accent)]">{r.pattern_key}</span>
                <Badge tone={impactoTone(r.priority)}>{impactoLabel(r.priority)}</Badge>
              </div>
              <span className="text-[12.5px] text-text">{r.summary}</span>
              <span className="text-[11px] text-text-muted">promovida em {r.promoted ?? '—'} · aguardando evidência real de aplicação</span>
            </div>
            <Badge tone="warning">Em quarentena</Badge>
            <ChevronRight size={16} className="shrink-0 text-text-muted" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Erros & quebras ───
function ErrosTab({ d }: { d: Autoaprendizado }) {
  const errs = d.erros;
  const ativos = errs.filter((e) => e.status === 'active');
  return (
    <div className="flex flex-col gap-5">
      {ativos.length === 0 && (
        <div className={cn('flex flex-col items-center gap-2.5 py-10 text-center', CARD)}>
          <span className="flex size-12 items-center justify-center rounded-xl" style={{ background: 'color-mix(in srgb, var(--ok) 14%, transparent)' }}>
            <ShieldCheck size={24} className="text-[var(--ok)]" />
          </span>
          <span className="text-[15px] font-semibold text-text">Sem quebra ativa conhecida</span>
          <span className="text-[12.5px] text-text-muted">Nenhum erro marcado como active. Nenhuma regra quebrou mais de uma vez.</span>
        </div>
      )}
      <SectionLabel>Histórico recente</SectionLabel>
      <div className="flex flex-col gap-3">
        {errs.map((e, i) => {
          const tone: Tone = e.status === 'resolved' ? 'ok' : e.status === 'active' ? 'danger' : 'warning';
          return (
            <div key={i} className={cn('flex items-start gap-3.5 p-[15px]', CARD)}>
              <span className="mt-1.5 size-[9px] shrink-0 rounded-full" style={{ background: TONE[tone] }} />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="text-[12px] font-semibold text-[var(--accent)]">{e.pattern_key ?? e.titulo}</span>
                  <Badge tone={tone}>{e.status ?? '—'}</Badge>
                  {e.recurrence != null && e.recurrence > 1 && <Badge tone="danger">quebrou {e.recurrence}×</Badge>}
                </div>
                <span className="text-[12.5px] text-text">{e.what_happened ?? e.titulo}</span>
                {e.do_differently && <span className="text-[11.5px] text-text-muted">↳ {e.do_differently}</span>}
              </div>
              <span className="shrink-0 text-[11px] text-text-muted">{e.last_seen ?? e.data ?? ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Melhorias ───
function MelhoriasTab({ d }: { d: Autoaprendizado }) {
  const lanes: Array<[string, string]> = [
    ['Feature do Hub', 'backlog_hub_feature'],
    ['Candidata a habilidade', 'backlog_skill_candidate'],
  ];
  return (
    <div className="flex flex-wrap items-start gap-4">
      {lanes.map(([label, status]) => {
        const items = d.features.filter((f) => f.status === status);
        return (
          <div key={status} className="flex flex-1 basis-[320px] flex-col gap-3">
            <div className="flex items-center gap-2">
              <SectionLabel>{label}</SectionLabel>
              <Badge tone="muted">{items.length}</Badge>
            </div>
            {items.map((f, i) => (
              <div key={f.id ?? i} className={cn('flex flex-col gap-2.5 p-[15px]', CARD)}>
                <span className="text-[13px] font-semibold text-text">{f.capability}</span>
                <div className="flex flex-wrap gap-1.5">
                  <Badge tone={f.priority === 'high' ? 'danger' : f.priority === 'medium' ? 'warning' : 'muted'}>Prio: {f.priority ?? '—'}</Badge>
                  <Badge tone="muted">Compl: {f.complexity ?? '—'}</Badge>
                  <Badge tone="muted">{f.frequency ?? '—'}</Badge>
                </div>
                {status === 'backlog_skill_candidate' && (
                  <span className="flex w-fit items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold text-[var(--accent)]" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>
                    <Zap size={12} /> candidata a habilidade
                  </span>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-text-muted">Nenhum item.</p>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Detalhe (drawer) ───
function RegraDrawer({ r, onClose }: { r: Regra; onClose: () => void }) {
  const st = statusView(r);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-[540px] flex-col overflow-y-auto bg-bg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <span className="text-[18px] font-bold text-text">{r.pattern_key}</span>
            <div className="flex gap-2">
              <Badge tone={impactoTone(r.priority)}>{impactoLabel(r.priority)}</Badge>
              <Badge tone={st.tone}>{st.label}</Badge>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-text-muted hover:bg-surface2"><X size={18} /></button>
        </div>
        <div className={cn('mt-5 flex flex-col gap-2 p-[18px]', CARD)}>
          <SectionLabel>Regra</SectionLabel>
          <p className="text-[14px] font-medium text-text">{r.summary}</p>
        </div>
        <div className={cn('mt-4 grid grid-cols-2 gap-4 p-[18px]', CARD)}>
          {[
            ['Origem', r.source], ['Status', r.status], ['Promoted target', r.promoted], ['Categoria', r.category],
            ['Recorrência', r.recurrence != null ? String(r.recurrence) : '—'], ['Application-Count', r.application_count != null ? String(r.application_count) : '0'],
            ['Last-Applied', r.last_applied ?? '—'], ['Última vez visto', r.last_seen ?? '—'],
          ].map(([k, v]) => (
            <div key={k as string} className="flex flex-col gap-0.5">
              <span className="text-[11px] text-text-muted">{k as string}</span>
              <span className="text-[13px] font-medium text-text">{(v as string) ?? '—'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AutoaprendizadoView() {
  const [tab, setTab] = useState<Tab>('Dashboard');
  const [sel, setSel] = useState<Regra | null>(null);
  const { data, isLoading, error } = useAutoaprendizado();

  const shell = (children: ReactNode, actions?: ReactNode) => (
    <div className="flex min-h-full flex-col bg-bg">
      <AgentesModuleTabs active="autoaprendizado" className="px-8 pt-3" />
      <div className="flex items-start justify-between gap-4 px-8 pb-4 pt-5">
        <h1 className="text-[18px] font-semibold leading-6 text-text">Qualidade do autoaprendizado</h1>
        {actions}
      </div>
      <div className="flex-1 px-8 pb-8">{children}</div>
    </div>
  );

  if (isLoading) return shell(<Card padding="lg"><p className="text-sm text-text-muted">Carregando…</p></Card>);
  if (error || !data) {
    const msg = (error as { status?: number })?.status === 403 ? 'Acesso restrito a administradores.' : 'Falha ao carregar o autoaprendizado.';
    return shell(<Card padding="lg"><p className="text-sm text-danger">{msg}</p></Card>);
  }

  const r = data.resumo;
  const statusPills = (
    <div className="flex gap-2">
      <span className="flex items-center gap-2 rounded-[10px] px-3 py-2" style={{ background: 'color-mix(in srgb, var(--ok) 12%, transparent)' }}>
        <Dot tone="ok" /><span className="text-[9.5px] uppercase text-text-muted">Estrutura</span><span className="text-[13px] font-bold text-text">{fmt(r.nota_estrutura)}/10</span>
      </span>
      <span className="flex items-center gap-2 rounded-[10px] px-3 py-2" style={{ background: 'color-mix(in srgb, var(--ok) 12%, transparent)' }}>
        <Dot tone="ok" /><span className="text-[9.5px] uppercase text-text-muted">Obediência</span><span className="text-[13px] font-bold text-text">{fmt(r.nota_obediencia)}/10</span>
      </span>
      <span className="flex items-center gap-2 rounded-[10px] px-3 py-2" style={{ background: 'color-mix(in srgb, var(--warning) 12%, transparent)' }}>
        <Dot tone="warning" /><span className="text-[9.5px] uppercase text-text-muted">Evidência</span><span className="text-[13px] font-bold text-text">{fmt(r.cobertura_evidencia_pct)}%</span>
      </span>
    </div>
  );

  return shell(
    <div className="flex flex-col gap-5">
      <div className="flex gap-6 border-b border-border">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={cn('relative pb-2.5 text-[13.5px] transition-colors', tab === t ? 'font-semibold text-text' : 'font-medium text-text-muted hover:text-text-2')}>
            {t}
            {tab === t && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[var(--accent)]" />}
          </button>
        ))}
      </div>
      {tab === 'Dashboard' && <DashboardTab d={data} />}
      {tab === 'Obediência' && <ObedienciaTab d={data} onSel={setSel} />}
      {tab === 'Quarentena' && <QuarentenaTab d={data} onSel={setSel} />}
      {tab === 'Erros & quebras' && <ErrosTab d={data} />}
      {tab === 'Melhorias' && <MelhoriasTab d={data} />}
      {sel && <RegraDrawer r={sel} onClose={() => setSel(null)} />}
    </div>,
    statusPills,
  );
}
